import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import LoginButtons from 'features/Login/LoginButtons'
import LoginErrorBox from 'features/Login/LoginErrorBox'
import { postLoginInfo, resetForm } from 'features/Login/LoginSlice'
import useFirstRender from 'hooks/useFirstRender'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, useParams } from 'react-router'

import PWAprompt from '../../components/PWAprompt/index'
import logo from '../../img/directful-logo.svg'
import {
  formatPhoneNumber,
  getGeoLocation,
  getGeoLocationPermissionStatus
} from '../../utils/helpers'
import AccountPicker from './AccountPicker'
import AvailableBrowser from './AvailableBrowser/index'
import EmailSentNotification from './EmailSentNotification/index'
import LoginHome from './LoginHome/index'
import LoginSlice from './LoginSlice'
import NotPhone from './NotPhone/index'
import PhoneLoginSentNotification from './PhoneLoginSentNotification/index'
import { getSavedAccounts } from './savedAccounts'

const Login = () => {
  const loginError = useSelector(s => s.signIn.error)
  const { isPhone } = useSelector(s => s.device)

  const isLoggedIn = useSelector(s => s.login.userLoggedIn)

  const { status } = useParams()

  const isLoginExpired = status === 'expired'
  const isMagicLinkErred = status === 'error'

  const [email, setEmail] = useState()
  const [phone, setPhone] = useState()
  const dispatch = useDispatch()

  const [savedAccounts, setSavedAccounts] = useState(() => getSavedAccounts())
  const [showAccountPicker, setShowAccountPicker] = useState(
    () => getSavedAccounts().length > 0
  )

  const [deferredPrompt, setDeferredPrompt] = useState()

  const loginStep = useSelector(s => s.signIn.loginStep)
  const loginType = useSelector(s => s.signIn.loginType)

  const [googleLoginError, setGoogleLoginError] = useState(null)
  const [microsoftLoginError, setMicrosoftLoginError] = useState(null)

  const geoLocation = useSelector(s => s.signIn.geoLocation)

  const [gettingGeoLocation, setGettingGeolocation] = useState(false)
  const isAvailableBrowser =
    navigator.userAgent.includes('Chrome') ||
    navigator.userAgent.includes('Edge') ||
    navigator.userAgent.includes('Firefox')

  const requestGeoLocation = useCallback(async () => {
    if (navigator?.permissions?.query) {
      setGettingGeolocation(true)

      const geoLocationPermission = await getGeoLocationPermissionStatus()

      if (
        geoLocationPermission === 'granted' &&
        localStorage.getItem('rememberUser') === 'true'
      ) {
        const location = await getGeoLocation()
        dispatch(LoginSlice.actions.saveGeoLocation(location))
      }

      setGettingGeolocation(false)
    }
  }, [dispatch])

  useEffect(() => {
    if (!geoLocation && !gettingGeoLocation && navigator?.permissions?.query) {
      requestGeoLocation()
    }
  }, [geoLocation, gettingGeoLocation, requestGeoLocation])

  useFirstRender(() => {
    if (navigator?.permissions?.query) {
      requestGeoLocation()
    }
  }, [geoLocation])

  const hasPWA = useSelector(s => s.signIn.hasPWA)

  useEffect(() => {
    window.addEventListener('beforeinstallprompt', e => {
      dispatch(LoginSlice.actions.hasPWA(false))
      e.preventDefault()
      setDeferredPrompt(e)
    })
  }, [dispatch])

  async function handleSubmitPhone(phone) {
    setPhone(formatPhoneNumber(phone))

    let cleanPhone = phone.replace(/\D+/g, '')

    let phoneToSend
    if (Number(cleanPhone.charAt(0)) === 1) {
      phoneToSend = cleanPhone
    } else {
      phoneToSend = `1${cleanPhone}`
    }

    requestGeoLocation()

    dispatch(postLoginInfo({ phone: phoneToSend, geoLocation }))

    localStorage.setItem('lastLoginType', 'phone')
    localStorage.setItem('lastLoginPhone', cleanPhone)
  }

  async function handleSubmitEmail(email) {
    setEmail(email)

    requestGeoLocation()

    dispatch(postLoginInfo({ email: email.toLowerCase(), geoLocation }))

    localStorage.setItem('lastLoginType', 'email')
    localStorage.setItem('lastLoginEmail', email)
  }

  function handleResetForm() {
    localStorage.removeItem('lastLoginType')
    localStorage.removeItem('lastLoginEmail')
    localStorage.removeItem('lastLoginPhone')

    dispatch(resetForm())
  }

  function handleSelectSavedAccount(email) {
    handleSubmitEmail(email)
  }

  function handleUseAnotherAccount() {
    setShowAccountPicker(false)
  }

  function handleAccountsChange() {
    const accounts = getSavedAccounts()
    setSavedAccounts(accounts)
    if (accounts.length === 0) {
      setShowAccountPicker(false)
    }
  }

  if (gettingGeoLocation) {
    return <LoadingIndicator />
  }

  if (isLoggedIn) {
    return <Redirect to="/" />
  }

  const LoginErrorDesc = () => (
    <>
      If problem persists{' '}
      <button onClick={() => window.$crisp.push(['do', 'chat:open'])}>click here</button>{' '}
      to contact us.
    </>
  )

  return (
    <div className="login-wrapper">
      {isLoginExpired && loginStep === 'sentRequestAndOpenedSocket' && (
        <LoginErrorBox
          title="Your login link was expired"
          description="Please log in again using the form below."
        />
      )}
      {isMagicLinkErred && (
        <LoginErrorBox title="Something went wrong" description={<LoginErrorDesc />} />
      )}
      {loginError && (
        <LoginErrorBox title={loginError} description={<LoginErrorDesc />} />
      )}
      {microsoftLoginError && !googleLoginError && !loginError && (
        <LoginErrorBox title={microsoftLoginError} description={<LoginErrorDesc />} />
      )}
      {googleLoginError && !microsoftLoginError && !loginError && (
        <LoginErrorBox title={googleLoginError} description={<LoginErrorDesc />} />
      )}
      {isPhone ? (
        <NotPhone />
      ) : !isAvailableBrowser ? (
        <AvailableBrowser />
      ) : (
        <>
          <LoginContainer>
            {(loginStep === 'sentRequest' || loginStep === 'sentRequestAndOpenedSocket') &&
              loginType === 'phone' && (
              <PhoneLoginSentNotification
                handleChangePhone={handleResetForm}
                userPhoneNumber={phone}
              />
            )}
            {(loginStep === 'sentRequest' || loginStep === 'sentRequestAndOpenedSocket') &&
              loginType === 'email' && (
              <EmailSentNotification
                handleChangeEmail={handleResetForm}
                userEmail={email}
              />
            )}
            {loginStep !== 'sentRequestAndOpenedSocket' &&
              showAccountPicker &&
              savedAccounts.length > 0 && (
                <AccountPicker
                  accounts={savedAccounts}
                  onSelectAccount={handleSelectSavedAccount}
                  onUseAnotherAccount={handleUseAnotherAccount}
                  onAccountsChange={handleAccountsChange}
                />
              )}
            {loginStep !== 'sentRequestAndOpenedSocket' &&
              (!showAccountPicker || savedAccounts.length === 0) && (
                <LoginHome
                  handleSubmitPhone={handleSubmitPhone}
                  handleSubmitEmail={handleSubmitEmail}
                  phoneNumber={phone}
                />
              )}
          </LoginContainer>
          <LoginButtons
            setGoogleLoginError={setGoogleLoginError}
            setMicrosoftLoginError={setMicrosoftLoginError}
          />
        </>
      )}
      <PWAprompt
        showPWAprompt={hasPWA === false}
        deferredPrompt={deferredPrompt}
        handleClick={() => dispatch(LoginSlice.actions.hasPWA(true))}
      />
    </div>
  )
}

const LoginContainer = ({ children }) => (
  <div className="login-container">
    <div className="login-head">
      <img src={logo} alt="Directful logo" />
      <div className="login-head__subtitle">dashboard</div>
    </div>
    {children}
  </div>
)

export default Login
